import React from 'react';
import { graphql, useFragment } from 'react-relay';

import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import { cssMap } from '@atlaskit/css';
import { Anchor, Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import EmailIcon from '@atlaskit/icon/core/email';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import type { NewCoreIconProps } from '@atlaskit/icon/base-new';
import Tooltip from '@atlaskit/tooltip';
import { projectTypeLabels } from '@atlassian/jira-projects-directory-v3-common/src/project-types/index.tsx';
import { useIntl, type IntlShape } from '@atlassian/jira-intl';
import type {
	RecommendationCard_recommendation$key,
	RecommendationCard_recommendation$data,
} from '@atlassian/jira-relay/src/__generated__/RecommendationCard_recommendation.graphql';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge/src/utils/fire-analytics.tsx';
import messages from './messages.tsx';
import { isJiraProjectStyle, isJiraProjectType } from './type-guards.tsx';

const styles = cssMap({
	card: {
		flex: '1 1 0',
		maxWidth: '259px',
		textDecoration: 'none',
		display: 'block',
		backgroundColor: token('elevation.surface'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.small'),

		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
			textDecoration: 'none',
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
			textDecoration: 'none',
		},
		'&:focus': {
			textDecoration: 'none',
		},
		'&:visited': {
			textDecoration: 'none',
		},
	},
	avatar: {
		flexShrink: 0,
		width: '24px', // Use of token blocked for width
		height: '24px', // Use of token blocked for height
		borderRadius: token('radius.xsmall'),
		display: 'inline-block',
		verticalAlign: 'middle',
	},
	nameSpacing: {
		marginTop: token('space.200'),
	},
	recommendationTypeSpacing: {
		marginTop: token('space.200'),
	},
});

// resolves the text and icon for the recommendation type
const getRecommendationDetails = ({
	details: { recommendationType, inviter },
	formatMessage,
}: {
	details: RecommendationCard_recommendation$data['details'];
	formatMessage: IntlShape['formatMessage'];
}): {
	text: string;
	Icon: React.ComponentType<NewCoreIconProps>;
} => {
	if (recommendationType === 'INVITER_ACTIVITY' && inviter?.name) {
		return {
			text: formatMessage(messages.recommendationTypeInviterActivity, {
				inviterName: inviter.name,
			}),
			Icon: EmailIcon,
		};
	}

	if (recommendationType === 'TENANT_ACTIVITY') {
		return {
			text: formatMessage(messages.recommendationTypeTenantActivity),
			Icon: PeopleGroupIcon,
		};
	}

	return {
		text: formatMessage(messages.recommendationTypeUnknown),
		Icon: QuestionCircleIcon,
	};
};

type Props = {
	recommendationRef: RecommendationCard_recommendation$key;
};

export const RecommendationCard: React.FC<Props> = ({ recommendationRef }) => {
	const { formatMessage } = useIntl();

	const data = useFragment<RecommendationCard_recommendation$key>(
		graphql`
			fragment RecommendationCard_recommendation on JiraProjectRecommendation {
				details @required(action: THROW) {
					... on JiraTenantActivityRecommendation {
						recommendationType @required(action: THROW)
					}
					... on JiraInviterActivityRecommendation {
						recommendationType @required(action: THROW)
						inviter {
							name @required(action: THROW)
						}
					}
				}
				project @required(action: THROW) {
					projectId @required(action: THROW)
					key @required(action: THROW)
					name @required(action: THROW)
					projectStyle @required(action: THROW)
					projectType @required(action: THROW)
					avatar @required(action: THROW) {
						medium @required(action: THROW)
					}
				}
			}
		`,
		recommendationRef,
	);

	const { projectId, name, projectStyle, projectType, key, avatar } = data.project;
	const { text: recommendationTypeText, Icon: RecommendationTypeIcon } = getRecommendationDetails({
		details: data.details,
		formatMessage,
	});

	const projectTypeText =
		isJiraProjectStyle(projectStyle) && isJiraProjectType(projectType)
			? formatMessage(projectTypeLabels[projectStyle][projectType])
			: formatMessage(messages.projectTypeUnknown);

	return (
		<Tooltip content={name}>
			{({ onClick: onTooltipClick, ...tooltipProps }) => (
				<Anchor
					href={`/browse/${key}`}
					xcss={styles.card}
					aria-label={formatMessage(messages.recommendationCardAriaLabel, {
						projectName: name,
						projectType: projectTypeText,
						recommendationType: recommendationTypeText,
					})}
					{...tooltipProps}
					onClick={(event, analyticsEvent) => {
						fireUIAnalytics(analyticsEvent, 'jiraProjectDirectoryRecommendation', {
							recommendationType: data.details.recommendationType,
							projectId,
						});
						onTooltipClick(event);
					}}
				>
					<Box xcss={styles.avatar} as="img" src={avatar.medium} alt="" />
					<Box xcss={styles.nameSpacing}>
						<Text weight="medium" size="medium" as="p" maxLines={1}>
							{name}
						</Text>
						<Text weight="regular" size="small" as="p" maxLines={1}>
							{projectTypeText}
						</Text>
					</Box>
					<Box xcss={styles.recommendationTypeSpacing}>
						<Text weight="regular" size="small">
							<RecommendationTypeIcon label="" size="small" /> {recommendationTypeText}
						</Text>
					</Box>
				</Anchor>
			)}
		</Tooltip>
	);
};
